import {
  canAccessAppForBillingStatus,
  canPurchaseWhiteLabelSetup as canPurchaseWhiteLabelSetupForStatus,
} from 'src/app/shared/billing/organization-status-policy';
import {
  normalizeWhiteLabelWorkflowStatus,
  OrganizationWhiteLabelWorkflowStatus,
} from 'src/app/shared/white-label/white-label-workflow';

export interface IOrganizationCalendarSettings {
  appointmentLength?: number;
  defaultDescription?: string;
  defaultLocation?: string;
  startTime?: string;
  viewMode?: "week" | "month";
}

export type OrganizationWhiteLabelAssetKey =
  | 'authLogoLight'
  | 'authLogoDark'
  /** @deprecated Prefer authLogoLight / authLogoDark. Kept for existing submissions. */
  | 'authLogo'
  | 'appIcon'
  | 'androidFeatureGraphic'
  | 'splashImage';

export interface IOrganizationWhiteLabelAssetRef {
  url: string;
  fileName: string;
}

export interface IOrganizationWhiteLabelSettings {
  /** Pipeline status for the branded app (see `OrganizationWhiteLabelWorkflowStatus`). */
  workflowStatus?: OrganizationWhiteLabelWorkflowStatus;
  submittedAt?: string;
  submittedBy?: string;
  lastModifiedAt?: string;
  lastModifiedBy?: string;
  appDisplayName?: string;
  /** iOS App Store subtitle (max 30 characters). */
  appSubtitle?: string;
  /** Google Play short description (max 80 characters). */
  appShortDescription?: string;
  /** Full App Store / Play Store description (max 4000 characters). */
  appDescription?: string;
  iosBundleId?: string;
  /** Numeric Apple App Store ID (e.g. `1462903750`) — used for download / store links. */
  iosAppStoreId?: string;
  androidPackageId?: string;
  storeListingOwnerName?: string;
  storeListingOwnerEmail?: string;
  supportEmail?: string;
  /** Public business website, when available. */
  websiteUrl?: string;
  /** Public page where clients can contact the business or get support. */
  contactUrl?: string;
  reviewContactEmail?: string;
  notes?: string;
  assets?: Partial<Record<OrganizationWhiteLabelAssetKey, IOrganizationWhiteLabelAssetRef>>;
}

interface IWorkHourDay {
  enabled: boolean;
  from: string;
  to: string;
}

export interface IProfileFieldOption {
  name: string;
  title: string;
  order: number;
}

export class Organization {
  public uid: string;
  public name: string;
  public title: string;
  public demo: boolean;
  public createdBy: string;
  public creationDate: string;
  public color?: string;
  public themeFont?: string;
  public features?: {[featureName: string]: boolean};
  public plan: "standard" | "pro" | "platinum";
  public status: string;
  public systemStatus?: "active" | "pendingDeletion" | "deleted"; // Used to manage deletion lifecycle
  public appLogo?: string;
  public referralAmount?: number;
  /** How the trainer heard about Elite Trainr at signup. */
  public source?: {
    source?: string;
    sourceOther?: string;
  };
  public accountExpirationDate?: string;
  public welcomeData?: {
    title: string,
    description: string,
    questions: any[];
    media: {
      type: "video" | "image",
      url: string
    }
    active: boolean,
    lastModified: string,
    lastModifiedBy: string,
  }
  public logo?: string;
  public registrationLink?: string;
  public owner?: string;
  public stripeStatus?: "enabled" | "incomplete";
  public auth: {
    admin: {
      members: string[]
    },
    trainers: {
      members: string[]
    }
  };
  public trainers: any;
  public clients: {
    [clientId: string]: boolean
  }
  public priorityClients: {
    [clientId: string]: "peak" | "high" | "moderate";
  }
  public activeClientCount?: number;
  public discounts?: { any };
  public trialEndDate?: string;
  /** When true, this org may use the white-label app setup form (queryable on the org root). */
  public whiteLabelEnabled?: boolean;
  billingTerms?: {
    acceptedOn: string;
    acceptedBy: string;
    terms: string;
  }
  public settings: {
    calendar?: IOrganizationCalendarSettings
    clients?: {
      defaultEquipmentList: boolean,
      defaultFoodPreferences: boolean,
      invoicingDisableAfter: number,
      profileFields?: {
        [fieldName: string]: {
          type: "select",
          name: string,
          value: string,
          emptyTitle?: string,
          options?: IProfileFieldOption[]
        }
      }
    },
    programs?: {
      cardioPrograms?: {
        defaultNotes: string[],
        disableInactivePrograms: boolean
      },
      disableInactivePrograms?: boolean,
      mealPlans?: {
        defaultNotes: string[]
      },
      workoutPrograms?: {
        defaultNotes: string[],
        categories: any 
        exerciseTypes: {
          [setId: string]: any;
        }
      },
      supplementProtocols?: {
        defaultNotes: string[]
      }
    },
    progressReports?: {
      followUpForm: string,
      field1: string;
      field2: string;
    },
    /** Client overview Metrics snapshot defaults (shared by all trainers in the org). */
    tracking?: {
      overviewStats?: string[];
    },
    services?: {
      services: any;
    };
    terms?: {
      content: string;
      publishedOn: string;
    },
    workHours?: {
      enabled: boolean,
      message: string,
      timezone: string;
      days: {
        monday: IWorkHourDay,
        tuesdau: IWorkHourDay,
        wednesday: IWorkHourDay,
        thursday: IWorkHourDay,
        friday: IWorkHourDay,
        saturday: IWorkHourDay,
        sunday: IWorkHourDay
      }
    },
    whiteLabel?: IOrganizationWhiteLabelSettings;
  }

  constructor(init: Partial<Organization>) {
    Object.assign(this, init);
  }

  isUserClient(userId: string): boolean {
    if (this.clients && Object.keys(this.clients).length > 0) {
      return this.clients.hasOwnProperty(userId)
    } else {
      return false
    }
  }

  isUserTrainer(userId: string): boolean {
    if (this.trainers && Object.keys(this.trainers).length > 0) {
      return this.trainers.hasOwnProperty(userId)
    } else {
      return false
    }
  }

  isUserAdmin(userId: string): boolean {
    return this.auth && this.auth.admin && this.auth.admin.members && this.auth.admin.members.hasOwnProperty(userId);
  }

  isUserOwner(userId: string) {
    return this.owner === userId
  } 

  getAdmins(): string[] {
    let admins = [];
    if (this.auth && this.auth.admin && this.auth.admin.members && this.auth.admin.members) {
      for (const userId of Object.keys(this.auth.admin.members)) {
        admins.push(userId);
      }
    }
    return admins
  }

  getClients(): string[] {
    let clients = [];
    if (this.clients && Object.keys(this.clients).length > 0) {
      for (const clientId in this.clients) {
        if (this.clients.hasOwnProperty(clientId) && this.clients[clientId] === true) {
          clients.push(clientId);
        }
      }
    }
    return clients
  }

  getTrainers(): string[] {
    let trainers = [];
    if (this.trainers && Object.keys(this.trainers).length > 0) {
      for (const trainerId in this.trainers) {
        if (this.trainers.hasOwnProperty(trainerId) && this.trainers[trainerId]) {
          trainers.push(trainerId);
        }
      }
    }
    return trainers
  }

  hasTerms(): boolean {
    return !!this.settings && !!this.settings.terms && !!this.settings.terms.publishedOn && !!this.settings.terms.content
  }

  inactiveProgramsDisabled(): boolean {
    return this.settings && this.settings.programs && this.settings.programs.disableInactivePrograms ? this.settings.programs.disableInactivePrograms : false
  }

  getSetting(settingName: string): any {
    return this.settings && this.settings[settingName] ? this.settings[settingName] : null;
  }

  getCurrentPlan(): string {
    return this.plan
  }

  setDefaultNotes(type: string, notes: any) {
    this.settings = this.settings || {};
    this.settings.programs = this.settings.programs || {};
    this.settings.programs[type] = this.settings.programs[type] || {};
    this.settings.programs[type].defaultNotes = notes;
  }

  getExericseTypes() {
    return this.settings?.programs?.workoutPrograms?.exerciseTypes || null
  }

  stripeEnabled(): boolean {
    return this.stripeStatus === "enabled";
  }

  hasFeatureAccess(featureName: string) {
    return !!(this.features && this.features[featureName] === true);
  }

  isPro() {
    return this.hasFeatureAccess("branding") || (this.plan === "pro" && canAccessAppForBillingStatus(this.status));
  }

  isTrialing() {
    return this.status === "trialing"
  }

  canPurchaseWhiteLabelSetup(): boolean {
    return canPurchaseWhiteLabelSetupForStatus(this.status, this.plan);
  }

  getCheckinForm() {
    return this.settings.progressReports?.followUpForm || undefined
  }

  getWhiteLabelSettings(): IOrganizationWhiteLabelSettings | null {
    return this.settings?.whiteLabel ?? null;
  }

  getWhiteLabelWorkflowStatus(): OrganizationWhiteLabelWorkflowStatus {
    const wl = this.settings?.whiteLabel;
    if (!wl) {
      return 'pending_information';
    }
    const raw = wl.workflowStatus ?? (wl as { status?: string }).status;
    return normalizeWhiteLabelWorkflowStatus(raw);
  }

  isWhiteLabelEnabled(): boolean {
    return this.whiteLabelEnabled === true;
  }
}